import { supabase } from '../lib/supabase';
import { getShippingSettings } from './shippingSettingsService';

// ============================================================================
// INTERFACES - Alinhadas com Admin Panel
// ============================================================================

export interface ShippingZone {
  id: string;
  name: string;
  countries: string[];        // Países da zona (ex: ['Switzerland', 'Germany'])
  base_cost: number;          // Custo base de frete
  active?: boolean;
  created_at?: string;
}

export interface PostalCodeInfo {
  postal_code: string;
  country: string;
  city?: string;
  region?: string;
}

export interface ShippingResult {
  cost: number | null;        // null = erro, não permite checkout
  zone: ShippingZone | null;
  zoneName: string;
  country: string;
  isFree: boolean;
  reason: string;
}

// ============================================================================
// MAPEAMENTO DE CÓDIGOS POSTAIS PARA PAÍSES
// ============================================================================

/**
 * Detecta o país baseado no formato do código postal
 * Suíça e países europeus comuns
 */
function detectCountryFromPostalCode(postalCode: string): string | null {
  const cleaned = postalCode.replace(/\s+/g, '').toUpperCase();
  
  // Suíça: 4 dígitos (1000-9999)
  if (/^[1-9]\d{3}$/.test(cleaned)) {
    return 'Switzerland';
  }
  
  // Alemanha: 5 dígitos (01000-99999)
  if (/^\d{5}$/.test(cleaned)) {
    return 'Germany';
  }
  
  // Áustria: 4 dígitos começando com 1-9
  if (/^[1-9]\d{3}$/.test(cleaned)) {
    // Conflito com Suíça, usar prefixos conhecidos
    const firstTwo = cleaned.substring(0, 2);
    if (['10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20'].includes(firstTwo)) {
      return 'Austria';
    }
    return 'Switzerland'; // Default para 4 dígitos
  }
  
  // França: 5 dígitos (começando com 01-95)
  if (/^(0[1-9]|[1-8]\d|9[0-5])\d{3}$/.test(cleaned)) {
    return 'France';
  }
  
  // Itália: 5 dígitos (00100-98999)
  if (/^[0-9]{5}$/.test(cleaned) && cleaned >= '00100' && cleaned <= '98999') {
    return 'Italy';
  }
  
  // Holanda: 4 dígitos + 2 letras (1234AB)
  if (/^\d{4}[A-Z]{2}$/.test(cleaned)) {
    return 'Netherlands';
  }
  
  // Bélgica: 4 dígitos (1000-9999)
  if (/^[1-9]\d{3}$/.test(cleaned)) {
    return 'Belgium';
  }
  
  // Reino Unido: formato variado (SW1A 1AA, EC1A 1BB, etc)
  if (/^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(cleaned)) {
    return 'United Kingdom';
  }
  
  // Espanha: 5 dígitos (01000-52999)
  if (/^(0[1-9]|[1-4]\d|5[0-2])\d{3}$/.test(cleaned)) {
    return 'Spain';
  }
  
  return null;
}

/**
 * Obtém informações do código postal
 * Para MVP, usa detecção por formato. Pode ser expandido com APIs de geocoding.
 */
async function getPostalCodeInfo(postalCode: string): Promise<PostalCodeInfo | null> {
  try {
    const country = detectCountryFromPostalCode(postalCode);
    
    if (!country) {
      console.warn(`⚠️ País não detectado para código postal: ${postalCode}`);
      return null;
    }
    
    return {
      postal_code: postalCode,
      country: country,
      city: 'Unknown',
      region: 'Unknown'
    };
  } catch (error) {
    console.error('❌ Erro ao processar código postal:', error);
    return null;
  }
}

// ============================================================================
// SHIPPING SERVICE
// ============================================================================

class ShippingService {
  /**
   * Busca todas as zonas de frete ativas no Supabase
   */
  async getActiveShippingZones(): Promise<ShippingZone[]> {
    try {
      const { data, error } = await supabase
        .from('shipping_zones')
        .select('*')
        .order('name');

      if (error) {
        console.error('❌ Erro ao buscar zonas de frete:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('❌ Erro ao consultar Supabase:', error);
      return [];
    }
  }

  /**
   * Encontra zona de frete por país
   */
  findZoneByCountry(zones: ShippingZone[], country: string): ShippingZone | null {
    const normalizedCountry = country.trim().toLowerCase();
    
    const zone = zones.find(z => {
      if (!z.countries || !Array.isArray(z.countries)) return false;
      
      return z.countries.some(c => 
        c.toLowerCase() === normalizedCountry
      );
    });

    return zone || null;
  }

  /**
   * Calcula frete baseado em código postal e valor do carrinho
   */
  async calculateShippingByPostalCode(
    postalCode: string, 
    cartTotal: number
  ): Promise<ShippingResult> {
    try {
      // 1. Validar código postal
      if (!postalCode || postalCode.length < 4) {
        return {
          cost: null,
          zone: null,
          zoneName: 'Invalid',
          country: 'Unknown',
          isFree: false,
          reason: 'Please enter a valid postal code to calculate shipping.'
        };
      }

      // 2. Detectar país pelo código postal
      const postalInfo = await getPostalCodeInfo(postalCode);
      
      if (!postalInfo || !postalInfo.country) {
        return {
          cost: null,
          zone: null,
          zoneName: 'Unknown',
          country: 'Unknown',
          isFree: false,
          reason: 'Country not identified. Please enter a valid European postal code.'
        };
      }

      // 3. Buscar zonas de frete
      const zones = await this.getActiveShippingZones();
      
      if (zones.length === 0) {
        console.warn('⚠️ Nenhuma zona de frete configurada');
        return {
          cost: null,
          zone: null,
          zoneName: 'No zones',
          country: postalInfo.country,
          isFree: false,
          reason: 'Shipping zones not configured. Please contact support.'
        };
      }

      // 4. Encontrar zona correspondente ao país
      const zone = this.findZoneByCountry(zones, postalInfo.country);
      
      if (!zone) {
        console.warn(`⚠️ Zona não encontrada para país: ${postalInfo.country}`);
        return {
          cost: null,
          zone: null,
          zoneName: 'Not covered',
          country: postalInfo.country,
          isFree: false,
          reason: `We don't ship to ${postalInfo.country} yet. Please contact us for a quote.`
        };
      }

      // 5. Calcular frete (base_cost da zona)
      let shippingCost = zone.base_cost || 0;

      // 5.1 Verificar frete grátis para Suíça acima do threshold
      if (postalInfo.country === 'Switzerland') {
        const settings = await getShippingSettings();
        if (cartTotal >= settings.free_shipping_threshold) {
          shippingCost = 0;
        }
      }

      // 6. Retornar resultado
      return {
        cost: shippingCost,
        zone: zone,
        zoneName: zone.name,
        country: postalInfo.country,
        isFree: shippingCost === 0,
        reason: shippingCost === 0 
          ? 'Free shipping to your region!' 
          : `Shipping cost: CHF ${shippingCost.toFixed(2)}`
      };

    } catch (error) {
      console.error('❌ Erro ao calcular frete:', error);
      
      // Em caso de erro, NÃO permitir checkout (retornar null)
      return {
        cost: null,
        zone: null,
        zoneName: 'Error',
        country: 'Unknown',
        isFree: false,
        reason: 'Error calculating shipping. Please try again or contact support.'
      };
    }
  }

  /**
   * Método simplificado para uso no carrinho
   * Retorna apenas o custo do frete
   * Retorna null se não foi possível calcular (bloqueia checkout)
   */
  async calculateCartShipping(
    postalCode: string | null, 
    cartTotal: number
  ): Promise<number | null> {
    if (!postalCode) {
      return null; // Sem código postal = não permite checkout
    }

    const result = await this.calculateShippingByPostalCode(postalCode, cartTotal);
    return result.cost; // Pode ser número ou null
  }

  /**
   * Valida formato de código postal (genérico para Europa)
   */
  isValidPostalCode(postalCode: string): boolean {
    if (!postalCode) return false;
    
    const cleaned = postalCode.replace(/\s+/g, '');
    
    // Aceita:
    // - 4-6 dígitos (Suíça, Áustria, Alemanha, etc)
    // - Alfanuméricos (UK: SW1A1AA, Holanda: 1234AB)
    return /^[A-Z0-9]{4,8}$/i.test(cleaned);
  }

  /**
   * Formata código postal
   */
  formatPostalCode(postalCode: string): string {
    return postalCode.replace(/\s+/g, '').toUpperCase();
  }
}

// Exportar instância singleton
export const shippingService = new ShippingService();
